import {useMemo} from 'react'
import {Empty, Space, Table, Typography} from 'antd'
import {ApartmentOutlined, DeleteOutlined, EditOutlined} from '@ant-design/icons'
import EllipsisTooltip from '../../common/EllipsisTooltip'
import RecordsCount from '../../common/RecordsCount'
import TableActions from '../../common/TableActions'
import {useIntl} from 'react-intl'

const HarnessTable = ({
  dataSource,
  pagination = false,
  getHarnessListData = null,
  onEdit = null,
  onDelete = null,
  onShowRelations = null,
}) => {
  const intl = useIntl()

  const columns = useMemo(
    () => [
      {
        title: (
          <EllipsisTooltip className="ellipsis">
            {intl.formatMessage({ id: 'harnesses.harnessId' })}
          </EllipsisTooltip>
        ),
        dataIndex: 'harness_id',
        sorter: (a, b) => (a.harness_id || '').localeCompare(b.harness_id || ''),
        render: (text) => (
          <div className="harness-id-cell">
            <EllipsisTooltip className="multiline-ellipsis" lineClamp={2}>
              {text || '-'}
            </EllipsisTooltip>
          </div>
        ),
      },
      {
        title: (
          <EllipsisTooltip className="ellipsis">
            {intl.formatMessage({ id: 'harnesses.partNumber' })}
          </EllipsisTooltip>
        ),
        dataIndex: 'part_number',
        // width: '17%',
        sorter: (a, b) => (a.part_number || '').localeCompare(b.part_number || ''),
        render: (text) => (
          <EllipsisTooltip className="ellipsis">
            {text || '-'}
          </EllipsisTooltip>
        ),
      },
      {
        title: (
          <EllipsisTooltip className="ellipsis">
            {intl.formatMessage({ id: 'harnesses.abbreviation' })}
          </EllipsisTooltip>
        ),
        dataIndex: 'abbreviation',
        // width: '14.5%',
        sorter: (a, b) => (a.abbreviation || '').localeCompare(b.abbreviation || ''),
        render: (text) => (
          <EllipsisTooltip className="ellipsis">
            {text || '-'}
          </EllipsisTooltip>
        ),
      },
      {
        title: (
          <EllipsisTooltip className="ellipsis">
            {intl.formatMessage({ id: 'harnesses.harnessType' })}
          </EllipsisTooltip>
        ),
        dataIndex: 'base_part_number',
        // width: '22.5%',
        sorter: (a, b) => (a.base_part_number || '').localeCompare(b.base_part_number || ''),
        render: (text) => (
          <EllipsisTooltip className="ellipsis">
            {text || '-'}
          </EllipsisTooltip>
        ),
      },
      {
        title: (
          <EllipsisTooltip className="ellipsis">
            {intl.formatMessage({ id: 'harnesses.action' })}
          </EllipsisTooltip>
        ),
        align: 'right',
        width: 120,
        // width: '136px',
        render: (_, record) => (
          <TableActions
            actions={[
              { 
                key: 'edit', 
                label: intl.formatMessage({ id: 'harnesses.edit' }), 
                icon: <EditOutlined />, 
                onClick: () => onEdit?.(record) 
              },
              { 
                key: 'show_relations', 
                label: intl.formatMessage({ id: 'harnesses.showRelatedData' }), 
                icon: <ApartmentOutlined />, 
                onClick: () => onShowRelations?.(record) 
              },
              { 
                key: 'delete', 
                label: intl.formatMessage({ id: 'harnesses.delete' }), 
                icon: <DeleteOutlined />, 
                danger: true, 
                onClick: () => onDelete?.(record) 
              },
            ]}
          />
        ),
      },
    ],
    [dataSource, pagination]
  )

  return (
    <Space
      direction="vertical"
      size="middle"
      style={{ width: '100%', verticalAlign: 'top' }}
    >
      <Table
        rowKey={(record) => `harness-${record.id}`}
        columns={columns}
        dataSource={dataSource}
        showSorterTooltip={false}
        tableLayout='auto'
        scroll={dataSource?.length ? { x: 900 } : undefined}
        pagination={
          pagination
            ? {
                current: pagination?.currentPage,
                total: pagination?.totalCount,
                pageSize: 10,
                showSizeChanger: false,
                hideOnSinglePage: true,
                showTotal: (total) => <RecordsCount count={total} />,
                onChange: (page) => {
                  const offset = (page - 1) * 10
                  const url = pagination?.nextPageLink
                    ? new URL(pagination?.nextPageLink)
                    : new URL(pagination?.prevPageLink)
                  url.searchParams.set('offset', offset)
                  getHarnessListData(url.toString())
                },
              }
            : false
        }
        locale={{
          emptyText: (
            <div style={{ padding: '40px' }}>
              <Empty description={<RecordsCount count={0} />} />
            </div>
          ),
        }}
      />
      {pagination && pagination?.totalPages <= 1 && pagination?.totalCount > 0 && (
        <Typography.Text>
          <RecordsCount count={pagination?.totalCount} />
        </Typography.Text>
      )}
    </Space>
  )
}

export default HarnessTable
